
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useServiceEditAccess = (serviceId: string | undefined) => {
  const [canAccessCalendarSettings, setCanAccessCalendarSettings] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isServiceOwner, setIsServiceOwner] = useState<boolean>(false);
  const [serviceHasCalendar, setServiceHasCalendar] = useState<boolean>(false);
  const [showEditInSidebar, setShowEditInSidebar] = useState<boolean>(false);
  const [canEditBasicInfo, setCanEditBasicInfo] = useState<boolean>(false);

  useEffect(() => {
    const checkAccess = async () => {
      if (!serviceId) {
        setCanAccessCalendarSettings(false);
        setShowEditInSidebar(false);
        setCanEditBasicInfo(false);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // Get current user
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData?.user) {
          setCanAccessCalendarSettings(false);
          setShowEditInSidebar(false);
          setCanEditBasicInfo(false);
          setIsLoading(false);
          return;
        }

        // Check if user is admin
        const { data: hasAdminRole, error: roleError } = await supabase.rpc('has_role', {
          '_role': 'admin'
        });

        if (roleError) {
          console.error('Error checking admin role:', roleError);
          setIsAdmin(false);
        } else {
          setIsAdmin(!!hasAdminRole);
        }

        // Check if service has calendar enabled and if user is the owner
        const { data: service, error: serviceError } = await supabase
          .from('services')
          .select('owner_id, calendar, vendor_can_edit')
          .eq('id', parseInt(serviceId))
          .single();
          
        if (serviceError || !service) {
          setCanAccessCalendarSettings(false);
          setServiceHasCalendar(false);
          setIsServiceOwner(false);
          setShowEditInSidebar(false);
          setCanEditBasicInfo(false);
        } else {
          setServiceHasCalendar(!!service.calendar);
          setIsServiceOwner(service.owner_id === userData.user.id);
          
          // User can access calendar settings if they are admin or the service owner
          setCanAccessCalendarSettings(
            (!!hasAdminRole) || 
            (service.owner_id === userData.user.id)
          );
          
          // Show edit in sidebar if they are admin or if they are the owner and can edit
          setShowEditInSidebar(
            (!!hasAdminRole) || 
            (service.owner_id === userData.user.id && !!service.vendor_can_edit)
          );
          
          // User can edit basic info if they are admin or if they are the owner and can edit
          setCanEditBasicInfo(
            (!!hasAdminRole) || 
            (service.owner_id === userData.user.id && !!service.vendor_can_edit)
          );
        }
      } catch (error) {
        console.error('Error checking service edit access:', error);
        setCanAccessCalendarSettings(false);
        setShowEditInSidebar(false);
        setCanEditBasicInfo(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAccess();
  }, [serviceId]);
  
  return { 
    canAccessCalendarSettings, 
    isLoading, 
    isAdmin, 
    isServiceOwner,
    serviceHasCalendar,
    showEditInSidebar,
    canEditBasicInfo,
    isOwner: isServiceOwner // Alias for isServiceOwner for backwards compatibility
  };
};
